import { useCallback, useContext, useEffect, useState, useRef } from "react";
import { useForm } from "react-hook-form";
import { Link, useNavigate } from "react-router-dom";
import { notification } from "antd";
import {
  DIALOG_NAME,
  useDialogSearchParam,
  useOpenParamDialog,
} from "../../helpers/useDialogSearchParam";
import CustomModal, { ModalDescription, ModalTitle } from "../CustomModal";
import { AuthContext } from "../../context/AuthProvider";
import ThirdPartyAuth from "./ThirdPartyAuth";
import { Spacer, FormError, Highlight } from "../../styles/CommonElements";
import styled from "styled-components";
import { device } from "../../lib/breakpoints";
import { trackEvent } from "../../helpers/mixpanel";
import ForgotPassword from "./ForgotPassword";
import useLoginMutation from "../../lib/useLoginMutation";
import Button from "../Button";
import RevealPassword from "../RevealPassword";
import { APP_NAME } from "../../helpers/constants";
import { APP_PRIVACY_ROUTE, APP_TERMS_ROUTE } from "../../helpers/routeNames";
import Logo from "../general/Logo";
import { formatEmailToLowercase } from "../../utils/emailUtils";

const LoginModal = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const [isPasswordRevealed, setPasswordRevealed] = useState(false);
  const [forgotPassword, setForgotPassword] = useState(false);
  const passwordRef = useRef();
  const openSignup = useOpenParamDialog(DIALOG_NAME.signup);

  const [api, contextHolder] = notification.useNotification();

  const { login, loginPending, loginError } = useLoginMutation({
    api,
  });

  const {
    register,
    handleSubmit,
    getValues,
    setValue,
    reset,
    formState: { isDirty, isValid, errors },
  } = useForm({
    mode: "onTouched",
  });

  const onSubmit = (data) => {
    login(data, {
      onSuccess: () => {
        reset();
        onClose();
      },
      onError: (error) => {
        console.error("Login error:", error);
        // reset dirty status but keep values (temporarily disabling login button)
        reset({}, { keepValues: true });
      },
    });
  };

  useEffect(() => {
    if (isOpen) {
      reset();
    }
  }, [isOpen, reset]);

  return (
    <>
      {contextHolder}
      <CustomModal
        isOpen={isOpen}
        onClose={() => {
          onClose();
          setForgotPassword(false);
        }}
        isCentered={true}
        width={500}
      >
        <LogoWrapper onClick={() => navigate("/")}>
          <Logo showTagline={false} size="large" displayMode="icon" color="" />
        </LogoWrapper>
        <Spacer $marginTop="0.5rem" />
        <StyledModalTitle>
          {!forgotPassword ? `Welcome to ${APP_NAME}` : "Reset your password"}
        </StyledModalTitle>
        <Spacer $marginTop="0.5rem" />
        {!forgotPassword && (
          <>
            <form onSubmit={handleSubmit(onSubmit)}>
              <InputHeader htmlFor="email">Email</InputHeader>
              <Spacer $marginTop="0.25rem" />
              <StyledInput
                id="email"
                placeholder="email@email.com"
                type="email"
                {...register(
                  "email",
                  // These inline validations can be replaced by Zod
                  {
                    required: "Please enter your email address",
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i,
                      message: "Invalid email address",
                    },
                  }
                )}
                onChange={(e) => {
                  const formattedEmail = formatEmailToLowercase(e.target.value);
                  if (formattedEmail !== e.target.value) {
                    setValue("email", formattedEmail);
                  }
                }}
              />
              {errors.email && <FormError>{errors.email.message}</FormError>}
              <Spacer $marginTop="1rem" />

              <InputHeader htmlFor="password">Password</InputHeader>
              <Spacer $marginTop="0.25rem" />
              <div style={{ position: "relative" }}>
                <StyledInput
                  id="password"
                  placeholder="Enter Password"
                  type={isPasswordRevealed ? "text" : "password"}
                  ref={passwordRef}
                  {...register("password", {
                    required: "Please enter your password",
                  })}
                />
                <RevealPassword
                  isRevealed={isPasswordRevealed}
                  toggleRevealed={() =>
                    setPasswordRevealed(!isPasswordRevealed)
                  }
                  passwordRef={passwordRef}
                />
              </div>
              {errors.password && (
                <FormError>{errors.password.message}</FormError>
              )}
              <ForgotPasswordSection>
                <Highlight
                  onClick={() => {
                    setForgotPassword(true);
                  }}
                >
                  Forgot password?
                </Highlight>
              </ForgotPasswordSection>

              <Spacer $marginTop="0.5rem" />

              {loginError !== "" && <LoginError>{loginError}</LoginError>}

              <Spacer $marginTop="1rem" />

              <LuxuryLoginButton
                type="submit"
                size="large"
                disabled={!isDirty || !isValid || loginPending}
              >
                Log In
              </LuxuryLoginButton>
            </form>

            {/*
            <Spacer $marginTop="1rem" />
            <ThirdPartyAuth cta="Log In" />
            */}
            <Spacer $marginTop="1rem" />

            <Terms>
              By continuing, you agree to our{" "}
              <Link to={APP_TERMS_ROUTE} target="_blank">
                <Highlight style={{ fontSize: "0.75rem !important" }}>
                  Terms of Service
                </Highlight>
              </Link>
              {" and "}
              <Link to={APP_PRIVACY_ROUTE} target="_blank">
                <Highlight style={{ fontSize: "0.75rem !important" }}>
                  Privacy Policy
                </Highlight>
              </Link>
              .
            </Terms>
            {/*
            <Spacer $marginTop="1rem" />
            <SignUpCta>
              <Highlight
                to="/signup"
                onClick={() => {
                  openSignup();
                }}
              >
                Don't have an account? Sign up here
              </Highlight>
            </SignUpCta>
            */}

            <Spacer $marginTop="0.25rem" />
          </>
        )}
        {forgotPassword && <ForgotPassword email={getValues("email")} />}
        <Spacer $marginTop="0.5rem" />
      </CustomModal>
    </>
  );
};

const LogoWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 1rem;
  margin-bottom: 1rem;
  height: 50px;
  width: auto;
`;

const ForgotPasswordSection = styled.div`
  text-align: right;
  width: 100%;
  font-size: 1rem;
  font-weight: 700;
  margin-top: 12px;
  margin-bottom: 0.5rem;

  span,
  a,
  .Highlight {
    color: var(--color-accent);
    cursor: pointer;
    font-weight: 700;
    font-size: 0.75rem !important;
    text-decoration: underline;
    transition: color 0.2s;

    &:hover {
      color: var(--color-primary);
    }
  }
`;

const LoginError = styled.div`
  font-family: var(--font-secondary);
  font-size: 1rem;
  font-weight: 700;
  line-height: 1.5;
  text-align: left;
  color: var(--color-error);
  margin-top: 0.5rem;
`;

const SignUpCta = styled.div`
  color: var(--color-text);
  text-align: center;
  font-family: var(--font-secondary);
  font-size: 14px;
  font-style: normal;
  font-weight: 600;
  line-height: 125%;

  span {
    cursor: pointer;
  }
`;

const LuxuryLoginButton = styled(Button)`
  background: var(--gradient-primary) !important;
  color: #fff !important;
  font-weight: 700;
  font-size: 1.1rem;
  border-radius: 12px;
  box-shadow: 0 4px 18px rgba(44, 95, 45, 0.15);
  border: none;
  width: 100%;
  margin-top: 0.5rem;
  font-family: var(--font-secondary);
  letter-spacing: -0.01em;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover:not(:disabled) {
    background: var(--gradient-primary-dark) !important;
    color: #fff !important;
    box-shadow: 0 8px 32px rgba(44, 95, 45, 0.25);
    transform: translateY(-2px);
  }

  &:disabled {
    background: var(--color-bg-tertiary) !important;
    color: var(--color-text-muted) !important;
    cursor: not-allowed;
    transform: none;
  }
`;

const StyledLogo = styled.img`
  width: 200px;
  height: auto;
`;

const StyledModalTitle = styled(ModalTitle)`
  color: var(--color-text);
  font-size: 1.65rem;
  font-weight: 600;
  text-align: center;
  margin-bottom: 0.5rem;
  letter-spacing: -0.5px;
  font-family: var(--font-primary);

  @media ${device.mobile} {
    font-size: 1.5rem;
  }
`;

const InputHeader = styled.label`
  color: var(--color-text);
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 0.25rem;
  display: block;
  @media ${device.mobile} {
    font-size: 1rem;
  }
`;

const StyledInput = styled.input`
  width: 100%;
  font-size: 1.1rem;
  padding: 1rem 1.25rem;
  border-radius: 12px;
  border: 1.5px solid var(--color-border-light);
  background: #fff;
  color: var(--color-text);
  font-family: var(--font-secondary);
  margin-bottom: 0.5rem;
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: var(--color-primary);
    box-shadow: 0 0 0 3px rgba(44, 95, 45, 0.1);
  }

  &::placeholder {
    color: var(--color-text-muted);
    opacity: 1;
    font-size: 1.05rem;
  }
`;

const Terms = styled.p`
  color: var(--color-text-light);
  text-align: center;
  font-size: 0.75rem !important;
  font-weight: 400;
  margin-top: 1.25rem;
  margin-bottom: 0.5rem;

  a,
  .Highlight {
    color: var(--color-primary);
    font-weight: 700;
    text-decoration: underline;
    font-size: 0.75rem !important;

    &:hover {
      color: var(--color-accent);
    }
  }
`;

export default LoginModal;
